import React, { useState, useEffect, useRef, forwardRef } from "react";
import Layout from "../../Components/Layout";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  AppBar,
  Toolbar,
  Slide,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QuestionAnswer as QuestionIcon,
  Refresh as RefreshIcon,
  Palette as PaletteIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Extension as IntegrationIcon,
  Widgets as WidgetsIcon,
  Science as ScienceIcon,
  VoiceChat as VoiceChatIcon,
  Forum as ForumIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { getAgencySuggestedQuestions } from "../../redux/actions/agencyActions";
import WidgetCore from "../Widget";
import AgencyDashboard from "./AgencyDashboard";
import AgencyPackages from "./Packages";
import Conversations from "../Conversations";
import Voices from "./Voices";
// import TestZone from "../TestZone";
import Integration from "./Integration1";
import {
  createSuggestedQuestion,
  updateSuggestedQuestion,
  deleteSuggestedQuestion,
  getWidgetColor,
  saveWidgetColor,
  saveTermsAndConditions,
  saveReturnEmail,
} from "../../redux/actions/widgetActions";
// import LinearLoading from '../Components/reuse/LinearLoading';
import useCallOnce from "../../hooks/useCallOnce";

function Widget({ selectedUserId }) {
  // Disable transition for tab-like instant view switching inside the dialog
  const FullScreenTransition = undefined;
  const dispatch = useDispatch();
  const dialogContainerRef = useRef(null);
  const {
    suggestedQuestions,
    loading,
    creating,
    pagination,
    widgetColor: reduxWidgetColor,
    widgetColorLoading,
    savingTerms,
  } = useSelector((state) => state.agencyWidget);
  const user = useSelector((state) => state?.auth?.user);

  // Local state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [questionText, setQuestionText] = useState("");
  const [questionType, setQuestionType] = useState("product");
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [deletingQuestion, setDeletingQuestion] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog state for viewing a specific referred user (same as Brands)
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedReferred, setSelectedReferred] = useState(null);
  const handleOpenDetail = (item) => {
    setSelectedReferred(item);
    setActiveTab(0);
    setDetailOpen(true);
  };
  const handleCloseDetail = () => {
    setDetailOpen(false);
  };
  const handleDialogExited = () => {
    // Clear selection when dialog has fully closed
    setSelectedReferred(null);
    setActiveTab(0);
  };

  // Color selection state
  const [widgetColor, setWidgetColor] = useState(reduxWidgetColor || "#000000");

  // Terms and conditions state
  const [termCondition, setTermCondition] = useState(user?.termCondition || "");

  // Return email state
  const [returnEmail, setReturnEmail] = useState(user?.returnEmail || "");
  const [emailError, setEmailError] = useState("");

  // Sync local widget color with Redux state
  useEffect(() => {
    if (reduxWidgetColor && reduxWidgetColor !== widgetColor) {
      // If we have a color from API and it's different from local, use it
      setWidgetColor(reduxWidgetColor);
    }
  }, [reduxWidgetColor]); // Only depend on reduxWidgetColor to prevent loops

  // Initialize color on mount
  useEffect(() => {
    if (reduxWidgetColor && !widgetColor) {
      setWidgetColor(reduxWidgetColor);
    }
  }, []); // Empty dependency array - runs only once on mount

  // Sync termCondition with user data
  useEffect(() => {
    if (user?.termCondition !== undefined) {
      setTermCondition(user.termCondition);
    }
  }, [user?.termCondition]);

  // Sync returnEmail with user data
  useEffect(() => {
    if (user?.returnEmail !== undefined) {
      setReturnEmail(user.returnEmail);
      setEmailError(""); // Clear any existing error when syncing with user data
    }
  }, [user?.returnEmail]);

  // Only fetch data for the active tab to avoid unnecessary API calls

  const lastFetchKeyRef = useRef("");
  useEffect(() => {
    if (!selectedUserId) return;
    // Only fetch when Widget tab is active
    if (activeTab !== 0) return;
    const fetchKey = `${selectedUserId}:${rowsPerPage}:1`;
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    setPage(0);
    dispatch(getAgencySuggestedQuestions(1, rowsPerPage, selectedUserId));
  }, [selectedUserId, rowsPerPage, activeTab, dispatch]);

  // useCallOnce("widget/getWidgetColor", () => {
  //   dispatch(getWidgetColor());
  // });

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    dispatch(getAgencySuggestedQuestions(newPage + 1, rowsPerPage, selectedUserId));
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    dispatch(getAgencySuggestedQuestions(1, newRowsPerPage, selectedUserId));
  };

  // Handle add question dialog open
  const handleAddDialogOpen = () => {
    setQuestionText("");
    setQuestionType("product");
    setAddDialogOpen(true);
  };

  // Handle add question dialog close
  const handleAddDialogClose = () => {
    setAddDialogOpen(false);
    setQuestionText("");
    setQuestionType("product");
  };

  // Handle question submission
  const handleSubmitQuestion = async () => {
    if (!questionText.trim()) return;

    const payload = {
      question: questionText.trim(),
      type: questionType,
    };

    await dispatch(createSuggestedQuestion(payload, selectedUserId , refreshQuestions ));
    handleAddDialogClose();
  };

  // Refresh questions list
  const refreshQuestions = () => {
    dispatch(getAgencySuggestedQuestions(page + 1, rowsPerPage, selectedUserId));
  };

  // Handle refresh button click
  const handleRefresh = () => {
    // Reset pagination to first page
    setPage(0);
    // Reset rows per page to default
    setRowsPerPage(10);
    // Refresh data from first page with loading state
    dispatch(getAgencySuggestedQuestions(1, 10, selectedUserId));
  };

  // Handle edit click
  const handleEditClick = (question) => {
    setEditingQuestion(question);
    setQuestionText(question.question);
    setQuestionType(question.type || "product");
    setEditDialogOpen(true);
  };

  // Handle edit dialog close
  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditingQuestion(null);
    setQuestionText("");
    setQuestionType("product");
  };

  // Handle edit submission
  const handleEditSubmit = async () => {
    if (!questionText.trim() || !editingQuestion) return;

    const payload = {
      question: questionText.trim(),
      type: questionType,
    };

    await dispatch(
      updateSuggestedQuestion(
        editingQuestion._id,
        payload,
        selectedUserId,
        refreshQuestions
      )
    );
    handleEditDialogClose();
  };

  // Handle delete click
  const handleDeleteClick = (question) => {
    setDeletingQuestion(question);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deletingQuestion) return;

    await dispatch(
      deleteSuggestedQuestion(
        deletingQuestion._id,
        selectedUserId,
        refreshQuestions
      )
    );
    setDeleteDialogOpen(false);
    setDeletingQuestion(null);
  };

  // Handle delete dialog close
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setDeletingQuestion(null);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get type color for chip
  const getTypeColor = (type) => {
    switch (type) {
      case "order":
        return "primary";
      case "product":
        return "success";
      case "support":
        return "warning";
      default:
        return "default";
    }
  };

  // Handle color change
  const handleColorChange = (event) => {
    const newColor = event.target.value;
    setWidgetColor(newColor);
  };

  // Handle save color
  const handleSaveColor = () => {
    dispatch(saveWidgetColor(widgetColor));
  };

  // Handle copy color to clipboard
  const handleCopyColor = async () => {
    try {
      await navigator.clipboard.writeText(widgetColor);
      // You can add a toast notification here if you want
    } catch (err) {
      console.error("Failed to copy color:", err);
    }
  };

  // Handle save terms and conditions
  const handleSaveTerms = () => {
    dispatch(saveTermsAndConditions(termCondition));
  };

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email change with validation
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setReturnEmail(value);

    if (value === "") {
      setEmailError("");
    } else if (!validateEmail(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  // Check if email is valid and not empty
  const isEmailValid =
    returnEmail.trim() !== "" && validateEmail(returnEmail) && !emailError;

  // Handle save return email
  const handleSaveReturnEmail = () => {
    if (isEmailValid) {
      dispatch(saveReturnEmail(returnEmail));
    }
  };

  return (
 <Box sx={{ p: { xs: 0, sm: 0 }, width: '100%', minWidth: 0, mt: 0 }}>
      {/* Header Section with Title and Description */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 0,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        {/* Left Side - Title and Description */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: "1.5rem", sm: "2rem" },
              color: "#111827",
            }}
          >
            User Management
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        {/* Left Side - Subheading */}
        {/* <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
              color: "#374151",
            }}
          >
            Manage suggested questions for your AI widget
          </Typography>
        </Box> */}

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            flexShrink: 0,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {/* <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              backgroundColor: "#ffffff",
              borderColor: "#d1d5db",
              color: "#374151",
              borderRadius: "25px",
              px: 3,
              py: 1.5,
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              width: { xs: "100%", sm: "auto" },
              "&:hover": {
                backgroundColor: "#f9fafb",
                borderColor: "#9ca3af",
              },
              "&:disabled": {
                backgroundColor: "#f9fafb",
                borderColor: "#e5e7eb",
                color: "#9ca3af",
              },
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddDialogOpen}
            sx={{
              backgroundColor: "#374151",
              color: "#ffffff",
              borderRadius: "25px",
              px: 3,
              py: 1.5,
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 600,
              border: "1px solid #000000",
              boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              width: { xs: "100%", sm: "auto" },
              "&:hover": {
                backgroundColor: "#1f2937",
                boxShadow:
                  "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
              },
              "&:active": {
                backgroundColor: "#111827",
                transform: "translateY(1px)",
              },
            }}
          >
            Add Question
          </Button> */}
        </Box>
      </Box>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>S. No</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>Company</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>Store URL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // Show centered loading spinner when refreshing
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: "center", py: 8 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body1" sx={{ color: "#6b7280" }}>
                        Loading...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : suggestedQuestions.length > 0 ? (
                suggestedQuestions.map((item, index) => {
                  const user = item.user || {};
                  return (
                    <TableRow
                      key={item._id || index}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleOpenDetail(item)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          {page * rowsPerPage + index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#374151", fontWeight: 500 }}>
                          {user.company || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          {user.email || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          {user.storeUrl || user.store_url || "-"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                // Show empty state when no data
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ color: "#6b7280", mb: 1 }}
                      >
                        No data found
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                        Try changing filters or refreshing
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={pagination.totalCount || pagination.totalItems || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: "1px solid #e5e7eb" }}
        />
      </Paper>

      {/* Detail Dialog (same behavior as Brands) */}
      {detailOpen && (
      <Dialog
        open
        onClose={handleCloseDetail}
        fullScreen
        TransitionComponent={FullScreenTransition}
        transitionDuration={{ appear: 0, enter: 0, exit: 0 }}
        sx={{ zIndex: (theme) => (theme?.zIndex?.modal || 1300) + 1 }}
      >
        <AppBar position="sticky" color="default" elevation={0} sx={{ borderBottom: "1px solid #e5e7eb" }}>
          <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={handleCloseDetail} edge="start" size="small" sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {(selectedReferred?.user?.company) || (selectedReferred?.user?.name) || "Details"}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDetail} edge="end" size="small">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <DialogContent ref={dialogContainerRef} key={selectedReferred?._id || selectedReferred?.user?._id || "none"} sx={{ p: 0, display: "flex", height: "100%" }}>
          {/* Side Menu */}
          <Box
            sx={{
              width: 250,
              borderRight: "1px solid #e5e7eb",
              backgroundColor: "#f9fafb",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ p: 2, borderBottom: "1px solid #e5e7eb" }}>
              <Typography variant="subtitle2" sx={{ color: "#6b7280", fontWeight: 600 }}>
                Agency Details
              </Typography>
            </Box>

            <Box sx={{ flex: 1, py: 1 }}>
              {[
                { label: "Dashboard", icon: <HomeIcon fontSize="small" /> },
                { label: "Widget", icon: <WidgetsIcon fontSize="small" /> },
                { label: "Conversations", icon: <ForumIcon fontSize="small" /> },
                { label: "Voices", icon: <VoiceChatIcon fontSize="small" /> },
                // { label: "Test Zone", icon: <ScienceIcon fontSize="small" /> },
                { label: "Integration", icon: <IntegrationIcon fontSize="small" /> },
                { label: "Packages", icon: <WidgetsIcon fontSize="small" /> },
              ].map((item, index) => (
                <Box
                  key={index}
                  onClick={() => setActiveTab(index)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    backgroundColor: activeTab === index ? "#111827" : "transparent",
                    color: activeTab === index ? "white" : "#374151",
                    "&:hover": { backgroundColor: activeTab === index ? "#111827" : "#f3f4f6" },
                    transition: "all 0.2s ease",
                    borderLeft: activeTab === index ? "3px solid #111827" : "3px solid transparent",
                  }}
                >
                  <Typography sx={{ mr: 1.5, fontSize: "16px", display: "flex", alignItems: "center" }}>
                    {item.icon}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: activeTab === index ? 600 : 400 }}>
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Content Area */}
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <style>{`
              .embedded-page header,
              .embedded-page nav[aria-label="sidebar"],
              .embedded-page .MuiDrawer-root,
              .embedded-page .MuiDrawer-paper {
                display: none !important;
              }
            `}</style>
            <Box className="embedded-page" sx={{ p: 0, height: "100%" }}>
              {activeTab === 0 && (
                <AgencyDashboard
                  selectedUserId={
                    (selectedReferred?.user?._id || selectedReferred?.user?.userId || selectedReferred?.user?.id) || undefined
                  }
                />
              )}
              {activeTab === 1 && (
                <WidgetCore
                  selectedUserId={(selectedReferred?.user?._id || selectedReferred?.user?.userId || selectedReferred?.user?.id) || undefined}
                />
              )}
              {activeTab === 2 && (
                <Conversations
                  selectedUserId={(selectedReferred?.user?._id || selectedReferred?.user?.userId || selectedReferred?.user?.id) || undefined}
                />
              )}
              {activeTab === 3 && (
                <Voices
                  embedded={true}
                  userId={
                    selectedReferred?.user?._id ||
                    selectedReferred?.user?.userId ||
                    selectedReferred?.user?.id ||
                    undefined
                  }
                />
              )}
              {/* {activeTab === 4 && <TestZone />} */}
              {activeTab === 4 && <Integration selectedUserId={(selectedReferred?.user?._id || selectedReferred?.user?.userId || selectedReferred?.user?.id) || undefined}/>}
              {activeTab === 5 && (
                <AgencyPackages
                  selectedUserId={(selectedReferred?.user?._id || selectedReferred?.user?.userId || selectedReferred?.user?.id) || undefined}
                />
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      )}

      {/* Add Question Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={handleAddDialogClose}
        maxWidth="sm"
        fullWidth
        container={() => dialogContainerRef.current}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            borderBottom: "1px solid #e5e7eb",
            pb: 2,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#222",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <QuestionIcon sx={{ fontSize: 20, color: "#ffffff" }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add Suggested Question
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel id="question-type-label">Question Type</InputLabel>
            <Select
              labelId="question-type-label"
              value={questionType}
              label="Question Type"
              onChange={(e) => setQuestionType(e.target.value)}
            >
              <MenuItem value="product">Product</MenuItem>
              <MenuItem value="order">Order</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Question"
            multiline
            rows={3}
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder="Enter your suggested question here..."
            variant="outlined"
            autoFocus
            onKeyDown={(e)=>{ if(e.key==='Enter' && (questionText||'').trim() && !creating){ e.preventDefault(); handleSubmitQuestion(); } }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: "1px solid #e5e7eb" }}>
          <Button
            onClick={handleAddDialogClose}
            color="inherit"
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitQuestion}
            variant="contained"
            disabled={!questionText.trim() || creating}
            startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
            sx={{ textTransform:'none' }}
          >
            {creating ? "Adding..." : "Add Question"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        maxWidth="sm"
        fullWidth
        container={() => dialogContainerRef.current}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            borderBottom: "1px solid #e5e7eb",
            pb: 2,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#222",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EditIcon sx={{ fontSize: 20, color: "#ffffff" }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Suggested Question
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel id="question-type-label">Question Type</InputLabel>
            <Select
              labelId="question-type-label"
              value={questionType}
              label="Question Type"
              onChange={(e) => setQuestionType(e.target.value)}
            >
              <MenuItem value="product">Product</MenuItem>
              <MenuItem value="order">Order</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Question"
            multiline
            rows={3}
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder="Enter your suggested question here..."
            variant="outlined"
          />
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: "1px solid #e5e7eb" }}>
          <Button
            onClick={handleEditDialogClose}
            color="inherit"
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={!questionText.trim() || creating}
            startIcon={creating ? <CircularProgress size={16} /> : <EditIcon />}
            // sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
          >
            {creating ? "Editing..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Question Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        maxWidth="sm"
        fullWidth
        container={() => dialogContainerRef.current}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            borderBottom: "1px solid #e5e7eb",
            pb: 2,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#ef4444",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DeleteIcon sx={{ fontSize: 20, color: "#ffffff" }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Confirm Deletion
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body1" sx={{ color: "#333", mb: 2 }}>
            Are you sure you want to delete this suggested question? This action
            cannot be undone.
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            Question: {deletingQuestion?.question}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            Type: {deletingQuestion?.type || "N/A"}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            Created At: {formatDate(deletingQuestion?.createdAt)}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: "1px solid #e5e7eb" }}>
          <Button onClick={handleDeleteDialogClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={creating}
            startIcon={
              creating ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {creating ? "Deleting..." : "Delete Question"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    // </Layout>
  );
}

export default Widget;
